import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { ThrottleClickDirective } from '@directives/throttle-click.directive';
import { Idea } from '@models/Sessions/Idea/Idea';
import { Session } from '@models/Sessions/Session';
import { ButtonModule } from 'primeng/button';
import { ButtonGroupModule } from 'primeng/buttongroup';
import { CardModule } from 'primeng/card';
import { DialogService } from 'primeng/dynamicdialog';
import { MessageModule } from 'primeng/message';
import { SpeedDialModule } from 'primeng/speeddial';
import { TooltipModule } from 'primeng/tooltip';
import { tap } from 'rxjs';

import { VideoClipComponent } from '../video-clip/video-clip.component';
import {
  createClipControl,
  VideoClipFormControl,
  VideoProjectFormService,
  VideoShotFormControl,
} from '../video-project.form';
import { VideoProjectService } from '../video-project.service';
import {
  VideoShotEditorComponent,
  VideoShotEditorFooterComponent,
} from './video-shot-editor/video-shot-editor.component';
@Component({
  selector: 'app-video-shot',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    SpeedDialModule,
    ButtonModule,
    ButtonGroupModule,
    MessageModule,
    TooltipModule,
    ThrottleClickDirective,
    VideoClipComponent,
  ],
  providers: [DialogService],
  templateUrl: './video-shot.component.html',
  styleUrl: './video-shot.component.scss',
})
export class VideoShotComponent implements OnInit {
  @Input() session: Session;
  @Input() idea: Idea;
  @Input() shot: FormGroup<VideoShotFormControl>;

  @Output() shotAdd = new EventEmitter();
  @Output() shotClone = new EventEmitter();
  @Output() shotDelete = new EventEmitter();
  @Output() shotReset = new EventEmitter();

  hasChanges: WritableSignal<boolean> = signal(false);

  get style() {
    if (this.shot.invalid) {
      return { outline: '2px solid var(--p-orange-500)' };
    }
    if (!this.shot.value.id) {
      return { outline: '2px solid var(--p-sky-100)' };
    }
    return {};
  }

  get isGenerating() {
    return this.clips.value.some(clip => {
      return ['generating'].includes(clip.asset.state);
    });
  }

  get isError() {
    return this.clips.value.some(clip => {
      return ['error'].includes(clip.asset.state);
    });
  }

  get clips() {
    return this.shot.controls.clips;
  }

  get selectedClipIndex() {
    if (this.shot.value.selectedClipIndex < 0) {
      return this.clips.controls.length - 1;
    }
    return this.shot.value.selectedClipIndex;
  }

  set selectedClipIndex(selectedClipIndex) {
    this.shot.controls.selectedClipIndex.setValue(selectedClipIndex);
  }

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly dialogService: DialogService,
    private readonly videoProjectService: VideoProjectService,
    private readonly videoProjectFormService: VideoProjectFormService,
  ) {}

  ngOnInit(): void {
    this.videoProjectFormService
      .$onShotChanges(this.shot)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([hasChanges]) => {
        this.hasChanges.set(hasChanges);
      });
  }

  onPrevClipClick() {
    this.selectedClipIndex = Math.max(this.selectedClipIndex - 1, 0);
  }

  onNextClipClick() {
    this.selectedClipIndex = Math.min(
      this.selectedClipIndex + 1,
      this.clips.length - 1,
    );
  }

  onClipGenerateClick() {
    this.videoProjectService
      .generateClip({
        sessionId: this.session._id,
        ideaId: this.idea._id,
        shotId: this.shot.value.id,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(({ shot }) => {
          this.shot.controls.clips.push(
            new FormGroup(
              createClipControl(shot.clips.at(shot.selectedClipIndex)),
            ),
          );
          this.shot.controls.selectedClipIndex.setValue(shot.selectedClipIndex);
        }),
        tap(() => {
          this.videoProjectFormService.setShotInitialValue(this.shot);
        }),
      )
      .subscribe();
  }

  onClipDeleteClick(clip: FormGroup<VideoClipFormControl>) {
    const index = this.findClipIndex(clip);

    if (index >= 0) {
      this.shot.controls.clips.removeAt(index);
      this.shot.controls.selectedClipIndex.setValue(
        Math.min(index, this.clips.length - 1),
      );
    }
  }

  onAddClick() {
    this.shotAdd.emit();
  }

  onCloneClick() {
    this.shotClone.emit();
  }

  onDeleteClick() {
    this.shotDelete.emit();
  }

  onResetClick() {
    this.shotReset.emit();
  }

  onEditClick() {
    const ref = this.dialogService.open(VideoShotEditorComponent, {
      header: 'Video Shot Editor',
      modal: true,
      draggable: false,
      resizable: false,
      maximizable: false,
      closeOnEscape: true,
      dismissableMask: true,
      closable: true,
      styleClass: 'w-2/4',
      data: {
        shot: this.shot,
        hasChanges: this.hasChanges,
      },
      templates: {
        footer: VideoShotEditorFooterComponent,
      },
    });
    ref.onClose.subscribe(action => {
      switch (action) {
        case 'reset':
          this.shotReset.emit();
          break;
        default:
          break;
      }
    });
  }

  private findClipIndex(clip: FormGroup<VideoClipFormControl>) {
    return this.shot.controls.clips.controls.indexOf(clip);
  }
}
